import React from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Badge,
  Button,
  ContextMenu,
  ContextMenuItem,
  Flashy,
  Gutter,
  HeaderBarLink,
  HeaderBarLinkSection,
  HeaderBarUserSection,
  Icon,
  ImageCircle,
  Layout,
  LayoutItem,
  Suspense,
  useApi,
  useApiWithErrors,
  Visible,
} from '@axiom/ui';
import { isLpUser } from '@axiom/utils';
import { PossibleImageSizes } from '@axiom/const';

import { EnvUtil } from '../../utils/env-util';
import { Notifications } from '../../components/Notifications/Notifications';
import { CandidateUtil } from '../../utils/candidate-util';
import { ProfileIssuesCounter } from '../../components/ProfileIssuesCounter/ProfileIssuesCounter';
import { PermissionsApi } from '../../api/permissions-api';
import { UserApi } from '../../api/user-api';
import { CandidateApi } from '../../api/candidate-api';
import { FeatureVisibilityUtil } from '../../utils/feature-visibility-util';
import { CandidateEngagementsApi } from '../../api/candidate-engagements-api';

import { AuthenticatedHeaderMenuItem } from './AuthenticatedHeaderMenuItem';

export const AuthenticatedHeaderMenu = () => {
  const navigate = useNavigate();
  const { candidateId: urlCandidateId } = useParams();

  const makeAbsoluteImpersonationUrl = (url: string) =>
    `/talent/${urlCandidateId ? `${urlCandidateId}/` : ''}${url}`;

  const [userData] = useApiWithErrors(UserApi.readUser());

  let candidateId = null;
  try {
    candidateId = CandidateUtil.reconcileUserCandidateId(
      userData instanceof Error ? undefined : userData.data,
      urlCandidateId
    );
  } catch {
    // Its okay if it can't find a candidateId
  }

  const [candidateResponse, apiPermissionResponse, engagementsCountResponse] =
    useApi(
      candidateId ? CandidateApi.readCandidate(candidateId) : undefined,
      candidateId ? PermissionsApi.readPermissions(candidateId) : undefined,
      candidateId
        ? CandidateEngagementsApi.readEngagementsCount(candidateId)
        : undefined
    );

  // Not throwing on Unauthorized 401 because its creating a render loop.
  // Anything else getting 401 for user will force a rerender to the proper
  // flow anyway.
  if (
    userData instanceof Error ||
    !candidateId ||
    !candidateResponse ||
    !apiPermissionResponse ||
    !engagementsCountResponse
  ) {
    return null;
  }

  const user = userData.data;
  const candidate = candidateResponse.data;
  const apiPermissions = apiPermissionResponse.data;
  const {
    engagements: engagementsActions,
    engagementsTotal,
    pastInterestHistoryTotal,
    pastEngagementHistoryTotal,
    pastSubmissionHistoryTotal,
  } = engagementsCountResponse.data;
  const hasEngagementsDataToShow =
    engagementsTotal +
      pastEngagementHistoryTotal +
      pastSubmissionHistoryTotal +
      pastInterestHistoryTotal >
    0;

  const shouldSeeLimitedFeatureSet =
    FeatureVisibilityUtil.shouldSeeLimitedFeatureSetBasedOnProfileStatus(
      candidate
    );

  return (
    <>
      <HeaderBarLinkSection>
        <AuthenticatedHeaderMenuItem
          name="NAVBARPROFILE"
          url={makeAbsoluteImpersonationUrl('profile')}
        >
          <Layout direction="horizontal" horizontalGutter="8px">
            Profile
            <Suspense fallback={null}>
              <ProfileIssuesCounter candidateId={candidateId} />
            </Suspense>
          </Layout>
        </AuthenticatedHeaderMenuItem>

        {!shouldSeeLimitedFeatureSet || hasEngagementsDataToShow ? (
          <AuthenticatedHeaderMenuItem
            name="NAVBARENGAGEMENTS"
            url={makeAbsoluteImpersonationUrl('engagements')}
          >
            <Layout direction="horizontal" horizontalGutter="8px">
              <LayoutItem>Engagements</LayoutItem>
              {engagementsActions > 0 ? (
                <LayoutItem>
                  <Badge
                    background="orange"
                    name="unactioned-opportunities-count"
                  >
                    {engagementsActions}
                  </Badge>
                </LayoutItem>
              ) : null}
            </Layout>
          </AuthenticatedHeaderMenuItem>
        ) : null}
        {apiPermissions.workday ? (
          <AuthenticatedHeaderMenuItem
            name="NAVBARTIMESHEETS"
            url="/talent/timesheets"
            target="timesheet"
          >
            Timesheets
          </AuthenticatedHeaderMenuItem>
        ) : null}
        {!!(
          apiPermissions.workday ||
          apiPermissions.seismic ||
          apiPermissions.lessonly ||
          apiPermissions.myApps
        ) && (
          <ContextMenu
            name="SSONAVBAREXISTS"
            direction="left"
            anchor={
              <HeaderBarLink name="nav-resources">Resources</HeaderBarLink>
            }
          >
            {!!apiPermissions.workday && (
              <ContextMenuItem
                name="SSONAVBAREXISTSBENEFITS"
                onClick={() => {
                  window
                    .open(
                      `${EnvUtil.apiPortalUrl}/saml/workday?RelayState=resourcesHub`,
                      'resources-hub'
                    )
                    ?.focus();
                }}
              >
                HR Help Center
              </ContextMenuItem>
            )}
            {!!apiPermissions.seismic && (
              <ContextMenuItem
                name="SSONAVBAREXISTSSEISMIC"
                onClick={() => {
                  window
                    .open(`${EnvUtil.apiPortalUrl}/saml/seismic`, 'seismic')
                    ?.focus();
                }}
              >
                Lessonly
              </ContextMenuItem>
            )}
            {!!apiPermissions.lessonly && (
              <ContextMenuItem
                name="SSONAVBAREXISTSLESSONLY"
                onClick={() => {
                  window
                    .open(`${EnvUtil.apiPortalUrl}/saml/lessonly`, 'lessonly')
                    ?.focus();
                }}
              >
                Lessonly
              </ContextMenuItem>
            )}
            {!!apiPermissions.workday && (
              <ContextMenuItem
                name="SSONAVBAREXISTSWORKDAY"
                onClick={() => {
                  window
                    .open(`${EnvUtil.apiPortalUrl}/saml/workday`, 'workday')
                    ?.focus();
                }}
              >
                Workday
              </ContextMenuItem>
            )}
            {!!apiPermissions.myApps && (
              <ContextMenuItem
                name="SSONAVBAREXISTSMYAPPS"
                onClick={() => {
                  window.open(`${EnvUtil.myAppsUrl}`, '_blank')?.focus();
                }}
              >
                My Apps
              </ContextMenuItem>
            )}
          </ContextMenu>
        )}
      </HeaderBarLinkSection>
      <HeaderBarUserSection>
        {isLpUser(user) && <Notifications />}
        <ContextMenu
          name="NAVBARDROPDOWN"
          direction="left"
          anchor={
            <>
              <ImageCircle
                name="NAVBARDROPDOWNPROFILEIMAGE"
                size="thumbnail"
                imageName={candidate.calculatedDisplayName}
                src={CandidateUtil.getProfileImageUri(
                  candidate,
                  PossibleImageSizes.W_160
                )}
              />
              <Visible only="smallScreen">
                <Gutter left="4px">
                  <Button
                    pattern="secondary"
                    variation="minimal"
                    name="NAVBARDROPDOWNNAME"
                  >
                    {candidate.calculatedDisplayName}
                  </Button>
                </Gutter>
              </Visible>
              <Gutter left="8px">
                <Flashy color="controlSecondary">
                  <Layout position="middle">
                    <Icon name="arrow-down" />
                  </Layout>
                </Flashy>
              </Gutter>
            </>
          }
        >
          <ContextMenuItem
            name="NAVBARDROPDOWNNOTIFICATIONPREFERENCES"
            onClick={() => {
              void navigate(makeAbsoluteImpersonationUrl('preferences'));
            }}
          >
            Preferences
          </ContextMenuItem>
          <ContextMenuItem
            name="NAVBARDROPDOWNREPORTISSUE"
            onClick={() => {
              window.location.href = `mailto:${EnvUtil.axiomAnswersEmail}`;
            }}
          >
            Report An Issue
          </ContextMenuItem>
          <ContextMenuItem
            name="NAVBARDROPDOWNLOGOUT"
            onClick={() => {
              window.location.href = '/logout';
            }}
          >
            Log Out
          </ContextMenuItem>
        </ContextMenu>
      </HeaderBarUserSection>
    </>
  );
};
